import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt$ = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

/**
 * Customer-facing PDF view. Contains ONLY customer-safe fields:
 *   number, dates, recipient, line names + qty + unit price + line total,
 *   subtotal, discount, delivery, tax, total, deposit, terms, acceptance area.
 * No costs, no margins, no recipe ingredients, no internal notes, no audit info.
 */
export default function QuotationPDF() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [err, setErr] = useState(null);

  const load = async () => {
    setState('loading'); setErr(null);
    try {
      setData(await apiFetch(`/quotations/${id}/pdf-data`));
      setState('ok');
    } catch (e) { setErr(e.message); setState('error'); }
  };
  useEffect(() => { load(); }, [id]);

  const markSent = async () => {
    try {
      await apiFetch(`/quotations/${id}/transition`, { method: 'POST', body: JSON.stringify({ action: 'mark_sent' }) });
      toast.success('Marked as sent');
    } catch (e) { toast.error(e.message); }
  };

  if (state === 'loading') return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-24 justify-center" data-testid="pdf-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Preparing PDF…
    </div>
  );
  if (state === 'error') return (
    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="pdf-error">
      {err} <Button onClick={load} variant="outline" className="rounded-sm ml-2">Retry</Button>
    </div>
  );

  const { quotation: q, customer, lines, terms } = data;

  return (
    <>
      <div className="print:hidden bg-background border-b border-border px-6 py-3 flex items-center justify-between sticky top-0 z-10" data-testid="pdf-toolbar">
        <button onClick={() => navigate(`/quotations/${id}`)} className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground velvet-eyebrow" data-testid="pdf-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Quotation
        </button>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={markSent} data-testid="pdf-mark-sent">Mark as sent</Button>
          <Button onClick={() => window.print()} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="pdf-print">
            <Printer className="h-4 w-4 mr-1" /> Download / Print
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-8 print:p-0 bg-white text-foreground" data-testid="quotation-pdf">
        <header className="flex justify-between items-start border-b border-foreground/20 pb-6">
          <div>
            <div className="font-serif text-3xl leading-tight tracking-tight">Velvet Bloom Flowers</div>
            <div className="text-xs text-muted-foreground mt-1">Signature floral atelier · Chicago</div>
          </div>
          <div className="text-right">
            <div className="velvet-eyebrow">Quotation</div>
            <div className="font-mono text-lg mt-1" data-testid="pdf-number">{q.number}</div>
            <div className="text-xs text-muted-foreground mt-1">Issued {fmtDate(q.created_at)}</div>
            <div className="text-xs text-muted-foreground">Valid until {fmtDate(q.valid_until)}</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 py-6 border-b border-foreground/10 text-sm">
          <div>
            <div className="velvet-eyebrow mb-2">For</div>
            <div className="font-serif text-base">{customer.name}</div>
            {customer.email && <div className="text-muted-foreground">{customer.email}</div>}
            {customer.phone && <div className="text-muted-foreground">{customer.phone}</div>}
          </div>
          <div>
            <div className="velvet-eyebrow mb-2">Fulfillment</div>
            <div className="text-sm">{q.fulfillment}</div>
            <div className="text-xs text-muted-foreground mt-1">Requested {fmtDate(q.requested_date)}</div>
            {q.recipient_name && <div className="text-xs mt-2"><span className="text-muted-foreground">Recipient</span> {q.recipient_name}</div>}
            {q.recipient_address && <div className="text-xs text-muted-foreground">{q.recipient_address}</div>}
            {q.recipient_phone && <div className="text-xs text-muted-foreground">{q.recipient_phone}</div>}
          </div>
        </section>

        <section className="py-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b-2 border-foreground/20">
                <th className="py-2 velvet-eyebrow">Item</th>
                <th className="py-2 velvet-eyebrow text-right w-16">Qty</th>
                <th className="py-2 velvet-eyebrow text-right w-24">Unit</th>
                <th className="py-2 velvet-eyebrow text-right w-24">Line</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-foreground/10" data-testid={`pdf-line-${i}`}>
                  <td className="py-3">
                    <div className="font-serif">{l.name}</div>
                    {l.description && <div className="text-xs text-muted-foreground">{l.description}</div>}
                  </td>
                  <td className="py-3 text-right velvet-money">{l.qty}</td>
                  <td className="py-3 text-right velvet-money">{fmt$(l.unit_price)}</td>
                  <td className="py-3 text-right velvet-money">{fmt$(l.line_total)}</td>
                </tr>
              ))}
              {lines.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-muted-foreground">No line items.</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="ml-auto max-w-xs text-sm space-y-1" data-testid="pdf-totals">
          <Row label="Subtotal" v={fmt$(q.subtotal)} />
          {q.discount_amount > 0 && <Row label="Discount" v={`- ${fmt$(q.discount_amount)}`} />}
          {q.delivery_fee > 0 && <Row label="Delivery" v={fmt$(q.delivery_fee)} />}
          {q.tax_amount != null && <Row label={`Tax${q.tax_rate ? ` (${(q.tax_rate*100).toFixed(2)}%)` : ''}`} v={fmt$(q.tax_amount)} />}
          <Row label="Total" v={fmt$(q.total)} bold />
          {q.deposit_amount != null && <Row label={`Deposit${q.deposit_percent ? ` (${q.deposit_percent}%)` : ''}`} v={fmt$(q.deposit_amount)} muted />}
        </section>

        {q.card_message && (
          <section className="py-6 mt-4 border-t border-foreground/10" data-testid="pdf-card-message">
            <div className="velvet-eyebrow mb-2">Card message</div>
            <p className="font-serif italic text-base">"{q.card_message}"</p>
          </section>
        )}

        <section className="py-6 mt-2 border-t border-foreground/10 text-xs text-muted-foreground" data-testid="pdf-terms">
          <div className="velvet-eyebrow mb-2 text-foreground">Reservation terms</div>
          <p className="leading-relaxed">{terms || '—'}</p>
        </section>

        <section className="pt-8 border-t border-foreground/20 grid grid-cols-2 gap-8" data-testid="pdf-acceptance">
          <div>
            <div className="velvet-eyebrow mb-2">Accepted by</div>
            <div className="border-b border-foreground/40 h-10"></div>
            <div className="text-[10px] text-muted-foreground mt-1">Signature</div>
          </div>
          <div>
            <div className="velvet-eyebrow mb-2">Date</div>
            <div className="border-b border-foreground/40 h-10"></div>
            <div className="text-[10px] text-muted-foreground mt-1">Print & sign, or reply to confirm.</div>
          </div>
        </section>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          nav, aside, [data-testid="sidebar"] { display: none !important; }
        }
      `}</style>
    </>
  );
}

function Row({ label, v, bold, muted }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? 'border-t border-foreground/40 pt-2 mt-2 font-medium' : ''}`}>
      <span className={muted ? 'text-muted-foreground' : ''}>{label}</span>
      <span className={`velvet-money ${muted ? 'text-muted-foreground' : ''}`}>{v}</span>
    </div>
  );
}
